import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Briefcase, Users, Code, Heart, MessageSquare, ArrowRight, CheckCircle } from 'lucide-react';
import Button from '../components/ui/Button';

const Careers: React.FC = () => {
  const positions = [
    {
      id: 1,
      title: 'Senior Full Stack Engineer',
      department: 'Engineering',
      location: 'New York, NY (Hybrid)',
      type: 'Full-time',
      description: 'Join our engineering team to build and scale our platform that connects communities through tool sharing.',
      requirements: [
        'Minimum 5 years of experience with modern web technologies',
        'Strong proficiency in React, Node.js, and TypeScript',
        'Experience with cloud platforms (AWS/GCP)',
        'Background in building marketplace or sharing economy platforms',
      ],
    },
    {
      id: 2,
      title: 'Community Success Manager',
      department: 'Operations',
      location: 'Remote (US)',
      type: 'Full-time',
      description: 'Help our community thrive by developing and implementing strategies to enhance user engagement and satisfaction.',
      requirements: [
        'Minimum 3 years of community management experience',
        'Strong communication and problem-solving skills',
        'Experience with community engagement tools',
        'Background in sharing economy or marketplace platforms',
      ],
    },
    {
      id: 3,
      title: 'Product Designer',
      department: 'Design',
      location: 'New York, NY (Hybrid)',
      type: 'Full-time',
      description: 'Create beautiful and intuitive user experiences that make tool sharing accessible to everyone.',
      requirements: [
        'Minimum 4 years of product design experience',
        'Strong portfolio showcasing UX/UI work',
        'Experience with design systems and component libraries',
        'Background in marketplace or community platforms',
      ],
    },
  ];

  const values = [
    {
      icon: <Users className="h-6 w-6" />,
      title: 'Community First',
      description: 'We believe in building strong, connected communities through sharing and collaboration.',
    },
    {
      icon: <Heart className="h-6 w-6" />,
      title: 'Sustainability',
      description: 'Our mission is to reduce waste and promote sustainable resource sharing.',
    },
    {
      icon: <Code className="h-6 w-6" />,
      title: 'Innovation',
      description: 'We\'re constantly pushing boundaries to make tool sharing more accessible and efficient.',
    },
    {
      icon: <MessageSquare className="h-6 w-6" />,
      title: 'Open Communication',
      description: 'We value transparency, honest feedback, and open dialogue at all levels.',
    },
  ];

  const benefits = [
    'Competitive salary and equity package',
    'Health, dental, and vision insurance',
    'Unlimited PTO policy',
    'Remote work flexibility',
    'Professional development budget',
    'Company-sponsored events and team building',
    'Wellness program and gym membership',
    'Parental leave',
    'Home office setup allowance',
    '401(k) matching',
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-green-600 text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
            alt="Team collaboration"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative container mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl mb-6">
              Join Our Mission
            </h1>
            <p className="text-xl text-green-100 mb-8">
              Help us build a more sustainable future through community-powered tool sharing. We're looking for passionate individuals who want to make a difference.
            </p>
            <Button 
              variant="secondary"
              size="lg"
              className="bg-white text-green-600 hover:bg-green-50"
            >
              View Open Positions
            </Button>
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="py-16 sm:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              These core values guide everything we do and shape our company culture
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value) => (
              <div key={value.title} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="text-green-600 mb-4">{value.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Benefits & Perks</h2>
            <p className="text-xl text-gray-600">
              We take care of our team so they can focus on taking care of our community
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit) => (
                <div key={benefit} className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Open Positions */}
      <div className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Open Positions</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Join our team and help build the future of community resource sharing
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              {positions.map((position) => (
                <div key={position.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{position.title}</h3>
                        <div className="mt-1 flex flex-wrap gap-2 text-sm text-gray-500">
                          <div className="flex items-center">
                            <Briefcase className="h-4 w-4 mr-1" />
                            <span>{position.department}</span>
                          </div>
                          <div className="flex items-center">
                            <MapPin className="h-4 w-4 mr-1" />
                            <span>{position.location}</span>
                          </div>
                          <span className="text-green-600 font-medium">{position.type}</span>
                        </div>
                      </div>
                      <div className="mt-4 sm:mt-0">
                        <Button 
                          variant="outline"
                          className="group"
                        >
                          <span>View Role</span>
                          <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </div>
                    </div>
                    <p className="mt-4 text-gray-600">{position.description}</p>
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Requirements:</h4>
                      <ul className="space-y-2">
                        {position.requirements.map((req, index) => (
                          <li key={index} className="flex items-start text-sm text-gray-600">
                            <CheckCircle className="h-4 w-4 text-green-500 mt-1 mr-2 flex-shrink-0" />
                            <span>{req}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-green-600 text-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Don't see the right role?</h2>
            <p className="text-xl text-green-100 mb-8">
              We're always looking for talented individuals to join our team. Send us your resume and we'll keep you in mind for future opportunities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                variant="secondary"
                size="lg"
                className="bg-white text-green-600 hover:bg-green-50"
              >
                Send Resume
              </Button>
              <Link to="/contact">
                <Button
                  variant="outline"
                  size="lg"
                  className="border-white text-white hover:bg-white hover:bg-opacity-10"
                >
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Careers;